import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown, Info, Plus, Sun, Moon, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { RequireCapability } from "@/components/RequireCapability";
import { Badge } from "@/features/expert/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { PrecisionNote } from "@/components/ui/precision-note";
import { Progress } from "@/components/ui/progress";
import { ProgressRing } from "@/components/ui/progress-ring";
import { toast } from "@/components/ui/sonner";
import { StatusPill, type StatusTone } from "@/components/ui/status-pill";
import { Stepper } from "@/components/ui/stepper";
import { Switch } from "@/components/ui/switch";
import { THEMATIC_KEYS, thematicClasses, type ThematicKey } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

/**
 * The SPA's living style guide — counterpart of the Django app's
 * `/guide-de-style/`.
 *
 * Every specimen is a real component from `components/ui` rendered against the
 * live tokens, so the page shows whatever the design system currently produces
 * and cannot go stale the way a screenshot would. Token values are read back
 * out of the DOM with `getComputedStyle`, which means they also follow the
 * theme: flip the preview to dark and the printed HSL triplets change with it.
 *
 * Specimen labels are the token and prop names you would type. They are code
 * identifiers, not copy, so they stay untranslated — only the headings and the
 * usage notes go through i18next.
 *
 * Internal reference for people building screens, not a member-facing page:
 * gated to the CQDD back-office, and deliberately not linked from the nav —
 * you reach it by typing the URL. Don't add a menu entry; the entry point is
 * CLAUDE.md.
 */

/** Semantic tokens, in the order the styling guidelines introduce them. */
const NEUTRAL_TOKENS = [
  "--background",
  "--foreground",
  "--card",
  "--muted",
  "--muted-foreground",
  "--secondary",
  "--border",
  "--input",
  "--ring",
] as const;

const SEMANTIC_TOKENS = [
  "--primary",
  "--success",
  "--warning",
  "--info",
  "--destructive",
  "--attention",
] as const;

/** The text colour each tone uses on its own tint. See globals.css. */
const INK_TOKENS = [
  "--success-ink",
  "--warning-ink",
  "--info-ink",
  "--destructive-ink",
] as const;

const BUTTON_VARIANTS = [
  "default",
  "secondary",
  "outline",
  "ghost",
  "link",
  "destructive",
] as const;

const BUTTON_SIZES = ["lg", "default", "sm"] as const;

const BADGE_TONES = [
  "neutral",
  "success",
  "warning",
  "info",
  "destructive",
  "attention",
] as const;

const PILL_TONES: readonly StatusTone[] = [
  "success",
  "warning",
  "muted",
  "destructive",
  "info",
] as const;

const TYPE_SCALE = [
  "text-xs",
  "text-sm",
  "text-base",
  "text-lg",
  "text-xl",
  "text-2xl",
  "text-3xl",
] as const;

/**
 * The resolved value of a CSS custom property, read from `el` so it reflects
 * the nearest theme scope. Re-read whenever the preview theme flips — a token
 * is redefined under `.dark`, and printing the light value there would be a
 * lie in exactly the place a reader is checking.
 */
function useCssVar(el: HTMLElement | null, name: string, dark: boolean) {
  const [value, setValue] = useState("");
  useEffect(() => {
    if (!el) return;
    setValue(getComputedStyle(el).getPropertyValue(name).trim());
  }, [el, name, dark]);
  return value;
}

function Swatch({ token, dark }: { token: string; dark: boolean }) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const value = useCssVar(el, token, dark);
  return (
    <div className="space-y-1.5">
      <div
        ref={setEl}
        className="h-14 rounded-lg border border-border"
        style={{ backgroundColor: `hsl(var(${token}))` }}
      />
      <code className="block text-xs font-semibold text-foreground">{token}</code>
      <span className="block font-mono text-[11px] text-muted-foreground">
        {value || "—"}
      </span>
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 space-y-4">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      {children}
    </section>
  );
}

/** One specimen: the live component, the classes/props that produce it, a note. */
function Specimen({
  code,
  note,
  children,
}: {
  code: string;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2 border-t border-border py-4 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-3">{children}</div>
      <code className="block break-words text-[11px] text-muted-foreground">
        {code}
      </code>
      {note && <p className="max-w-prose text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}

const SECTION_IDS = [
  "colors",
  "typography",
  "shape",
  "buttons",
  "dropdowns",
  "badges",
  "status",
  "alerts",
  "cards",
  "forms",
  "progress",
  "stepper",
  "modal",
  "thematics",
] as const;

function StyleGuide() {
  const { t } = useTranslation();
  const [dark, setDark] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [switchOn, setSwitchOn] = useState(true);
  const [address, setAddress] = useState("");
  const [step, setStep] = useState("one");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("styleGuide.title")}
        </h1>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDark((d) => !d)}
          aria-pressed={dark}
        >
          {dark ? <Sun aria-hidden /> : <Moon aria-hidden />}
          {dark ? t("styleGuide.previewLight") : t("styleGuide.previewDark")}
        </Button>
      </div>

      <Card>
        <CardContent className="space-y-2 pt-6 text-sm">
          <p>{t("styleGuide.intro")}</p>
          <p>
            {t("styleGuide.introSources")}{" "}
            <code className="text-xs">src/styles/globals.css</code>,{" "}
            <code className="text-xs">tailwind.config.ts</code>,{" "}
            <code className="text-xs">src/lib/thematic/index.ts</code>.
          </p>
          <p className="text-muted-foreground">{t("styleGuide.introDark")}</p>
        </CardContent>
      </Card>

      <nav aria-label={t("styleGuide.sections")} className="flex flex-wrap gap-2">
        {SECTION_IDS.map((id) => (
          <a
            key={id}
            href={`#${id}`}
            className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t(`styleGuide.nav.${id}`)}
          </a>
        ))}
      </nav>

      {/*
        The specimen stage. `dark` re-declares every token for this subtree, so
        the preview switches theme without touching the surrounding shell — and
        without a global toggle the app does not otherwise have.
      */}
      <div
        className={cn(
          "space-y-10 rounded-xl border border-border bg-background p-4 text-foreground sm:p-6",
          dark && "dark",
        )}
      >
        <Section id="colors" title={t("styleGuide.nav.colors")}>
          <p className="max-w-prose text-sm text-muted-foreground">
            {t("styleGuide.colorsNote")}
          </p>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("styleGuide.neutrals")}
          </h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {NEUTRAL_TOKENS.map((token) => (
              <Swatch key={token} token={token} dark={dark} />
            ))}
          </div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("styleGuide.semantic")}
          </h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {SEMANTIC_TOKENS.map((token) => (
              <Swatch key={token} token={token} dark={dark} />
            ))}
          </div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("styleGuide.inks")}
          </h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {INK_TOKENS.map((token) => (
              <Swatch key={token} token={token} dark={dark} />
            ))}
          </div>
          <p className="max-w-prose text-xs text-muted-foreground">
            {t("styleGuide.inkNote")}
          </p>
          <p className="max-w-prose text-xs text-muted-foreground">
            {t("styleGuide.primaryVsDestructive")}
          </p>
        </Section>

        <Section id="typography" title={t("styleGuide.nav.typography")}>
          <Specimen code="font-sans (Open Sans)" note={t("styleGuide.sansNote")}>
            <span className="font-sans text-lg">
              Open Sans — abcdefghijklmnopqrstuvwxyz 0123456789
            </span>
          </Specimen>
          <Specimen code="font-serif" note={t("styleGuide.serifNote")}>
            <span className="font-serif text-lg">
              Georgia — abcdefghijklmnopqrstuvwxyz 0123456789
            </span>
          </Specimen>
          <Specimen code={TYPE_SCALE.join(" · ")}>
            <div className="space-y-1">
              {TYPE_SCALE.map((cls) => (
                <div key={cls} className={cn(cls, "leading-tight")}>
                  {cls}
                </div>
              ))}
            </div>
          </Specimen>
          <Specimen
            code="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            note={t("styleGuide.eyebrowNote")}
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("styleGuide.eyebrowSample")}
            </span>
          </Specimen>
          <Specimen code="tabular-nums" note={t("styleGuide.tabularNote")}>
            <span className="tabular-nums text-sm">18 / 42 · 1 204 · 99 %</span>
          </Specimen>
        </Section>

        <Section id="shape" title={t("styleGuide.nav.shape")}>
          <Specimen
            code="rounded-md · rounded-lg · rounded-xl · rounded-full"
            note={t("styleGuide.radiusNote")}
          >
            {(["rounded-md", "rounded-lg", "rounded-xl", "rounded-full"] as const).map(
              (r) => (
                <div key={r} className="space-y-1.5 text-center">
                  <div className={cn("h-14 w-24 bg-muted-foreground/40", r)} />
                  <code className="text-[11px] text-muted-foreground">{r}</code>
                </div>
              ),
            )}
          </Specimen>
          <Specimen code="shadow-sm · shadow-md" note={t("styleGuide.elevationNote")}>
            {(["shadow-sm", "shadow-md"] as const).map((s) => (
              <div key={s} className="space-y-1.5 text-center">
                <div className={cn("h-14 w-24 rounded-lg border bg-card", s)} />
                <code className="text-[11px] text-muted-foreground">{s}</code>
              </div>
            ))}
          </Specimen>
        </Section>

        <Section id="buttons" title={t("styleGuide.nav.buttons")}>
          <Specimen
            code={BUTTON_VARIANTS.map((v) => `variant="${v}"`).join(" · ")}
            note={t("styleGuide.buttonVariantNote")}
          >
            {BUTTON_VARIANTS.map((variant) => (
              <Button key={variant} variant={variant}>
                {variant}
              </Button>
            ))}
          </Specimen>
          <Specimen
            code={`${BUTTON_SIZES.map((s) => `size="${s}"`).join(" · ")} · size="icon"`}
            note={t("styleGuide.buttonSizeNote")}
          >
            {BUTTON_SIZES.map((size) => (
              <Button key={size} size={size}>
                {size}
              </Button>
            ))}
            <Button size="icon" aria-label={t("styleGuide.addSample")}>
              <Plus aria-hidden />
            </Button>
          </Specimen>
          <Specimen code="disabled · [&_svg]:size-4">
            <Button disabled>disabled</Button>
            <Button variant="outline" disabled>
              disabled
            </Button>
            <Button variant="destructive">
              <Trash2 aria-hidden />
              {t("styleGuide.deleteSample")}
            </Button>
          </Specimen>
        </Section>

        <Section id="dropdowns" title={t("styleGuide.nav.dropdowns")}>
          <Specimen
            code="<DropdownMenu> > <DropdownMenuTrigger asChild> + <DropdownMenuContent align> > <DropdownMenuItem>"
            note={t("styleGuide.dropdownNote")}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  {t("styleGuide.dropdownTrigger")}
                  <ChevronDown aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>{t("styleGuide.dropdownLabel")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>{t("styleGuide.firstOption")}</DropdownMenuItem>
                <DropdownMenuItem>{t("styleGuide.secondOption")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Specimen>
        </Section>

        <Section id="badges" title={t("styleGuide.nav.badges")}>
          <Specimen
            code={BADGE_TONES.map((tone) => `tone="${tone}"`).join(" · ")}
            note={t("styleGuide.badgeNote")}
          >
            {BADGE_TONES.map((tone) => (
              <Badge key={tone} tone={tone}>
                {tone}
              </Badge>
            ))}
          </Specimen>
        </Section>

        <Section id="status" title={t("styleGuide.nav.status")}>
          <Specimen
            code={PILL_TONES.map((tone) => `tone="${tone}"`).join(" · ")}
            note={t("styleGuide.statusNote")}
          >
            {PILL_TONES.map((tone) => (
              <StatusPill key={tone} tone={tone}>
                {tone}
              </StatusPill>
            ))}
          </Specimen>
        </Section>

        <Section id="alerts" title={t("styleGuide.nav.alerts")}>
          <Specimen
            code="rounded-lg border border-info/30 bg-info/10 p-3 text-info-ink"
            note={t("styleGuide.alertNote")}
          >
            <div className="flex w-full items-start gap-2 rounded-lg border border-info/30 bg-info/10 p-3 text-sm text-info-ink">
              <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
              <span>{t("styleGuide.alertSample")}</span>
            </div>
          </Specimen>
          <Specimen code="border-destructive/30 bg-destructive/10 text-destructive-ink">
            <div className="w-full rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive-ink">
              {t("styleGuide.errorSample")}
            </div>
          </Specimen>
          <Specimen
            code='toast.success(…) · toast.error(…) — from "@/components/ui/sonner"'
            note={t("styleGuide.toastNote")}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={() => toast.success(t("styleGuide.toastSuccessSample"))}
            >
              toast.success
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toast.error(t("styleGuide.toastErrorSample"))}
            >
              toast.error
            </Button>
          </Specimen>
        </Section>

        <Section id="cards" title={t("styleGuide.nav.cards")}>
          <Specimen
            code="<Card> > <CardHeader> > <CardTitle> + <CardDescription> ; <CardContent>"
            note={t("styleGuide.cardNote")}
          >
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>CardTitle</CardTitle>
                <CardDescription>{t("styleGuide.cardDescription")}</CardDescription>
              </CardHeader>
              <CardContent className="pt-4 text-sm">
                {t("styleGuide.cardBody")}
              </CardContent>
            </Card>
          </Specimen>
        </Section>

        <Section id="forms" title={t("styleGuide.nav.forms")}>
          <Specimen
            code="<Label> + <Input> · aria-invalid"
            note={t("styleGuide.labelNote")}
          >
            <div className="w-full max-w-md space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="sg-name">
                  {t("styleGuide.fieldLabel")}{" "}
                  <span className="text-primary">*</span>
                </Label>
                <Input id="sg-name" placeholder={t("styleGuide.fieldPlaceholder")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sg-invalid">aria-invalid</Label>
                <Input id="sg-invalid" aria-invalid defaultValue="" />
                <p className="text-xs text-destructive">
                  {t("styleGuide.fieldError")}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sg-disabled">disabled</Label>
                <Input id="sg-disabled" disabled defaultValue="—" />
              </div>
            </div>
          </Specimen>
          <Specimen
            code="<AddressAutocomplete value onChange onResolved>"
            note={t("styleGuide.addressNote")}
          >
            <div className="w-full max-w-md space-y-1.5">
              <Label htmlFor="sg-address">{t("styleGuide.addressLabel")}</Label>
              <AddressAutocomplete
                id="sg-address"
                value={address}
                onChange={setAddress}
                onResolved={(result, suggestion) =>
                  setAddress(result.confirmed ? result.formatted_address : suggestion.text)
                }
              />
            </div>
          </Specimen>
          <Specimen code="<Switch checked onCheckedChange>">
            <Switch
              checked={switchOn}
              onCheckedChange={setSwitchOn}
              label={t("styleGuide.switchLabel")}
            />
            <span className="text-sm">{t("styleGuide.switchLabel")}</span>
          </Specimen>
          <Specimen code="<PrecisionNote text>" note={t("styleGuide.precisionNote")}>
            <PrecisionNote text={t("styleGuide.precisionSample")} />
          </Specimen>
        </Section>

        <Section id="progress" title={t("styleGuide.nav.progress")}>
          <Specimen code="<Progress value> · indicatorClassName">
            <div className="w-full max-w-md space-y-3">
              <Progress value={45} />
              <Progress value={72} indicatorClassName="bg-success" />
            </div>
          </Specimen>
          <Specimen code="<ProgressRing value>">
            <ProgressRing value={45} />
            <ProgressRing value={100} />
          </Specimen>
        </Section>

        <Section id="stepper" title={t("styleGuide.nav.stepper")}>
          <Specimen code="<Stepper steps current onSelect>">
            <Stepper
              ariaLabel={t("styleGuide.nav.stepper")}
              current={step}
              onSelect={setStep}
              steps={[
                { key: "one", label: t("styleGuide.step1") },
                { key: "two", label: t("styleGuide.step2") },
                { key: "three", label: t("styleGuide.step3") },
              ]}
            />
          </Specimen>
        </Section>

        <Section id="modal" title={t("styleGuide.nav.modal")}>
          <Specimen code="<Modal title onClose>" note={t("styleGuide.modalNote")}>
            <Button variant="outline" onClick={() => setModalOpen(true)}>
              {t("styleGuide.openModal")}
            </Button>
          </Specimen>
          {modalOpen && (
            <Modal
              title={t("styleGuide.modalTitle")}
              onClose={() => setModalOpen(false)}
            >
              <p className="text-sm">{t("styleGuide.modalBody")}</p>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setModalOpen(false)}>
                  {t("styleGuide.cancel")}
                </Button>
                <Button onClick={() => setModalOpen(false)}>
                  {t("styleGuide.confirm")}
                </Button>
              </div>
            </Modal>
          )}
          <Specimen
            code="<ConfirmDialog title message confirmLabel cancelLabel onConfirm onClose>"
            note={t("styleGuide.confirmDialogNote")}
          >
            <Button variant="outline" onClick={() => setConfirmOpen(true)}>
              {t("styleGuide.openConfirmDialog")}
            </Button>
          </Specimen>
          {confirmOpen && (
            <ConfirmDialog
              title={t("styleGuide.confirmDialogTitle")}
              message={t("styleGuide.confirmDialogBody")}
              confirmLabel={t("styleGuide.deleteSample")}
              cancelLabel={t("styleGuide.cancel")}
              onConfirm={() => setConfirmOpen(false)}
              onClose={() => setConfirmOpen(false)}
            />
          )}
        </Section>

        <Section id="thematics" title={t("styleGuide.nav.thematics")}>
          <p className="max-w-prose text-sm text-muted-foreground">
            {t("styleGuide.thematicsNote")}
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {THEMATIC_KEYS.map((key: ThematicKey) => {
              const tc = thematicClasses(key);
              return (
                <div
                  key={key}
                  className={cn("space-y-2 rounded-lg border p-3", tc.border)}
                >
                  <div
                    className={cn(
                      "flex h-12 items-center justify-center rounded-md text-sm font-semibold",
                      tc.solid,
                      tc.onSolid,
                    )}
                  >
                    solid
                  </div>
                  <div
                    className={cn(
                      "rounded-md px-2 py-1 text-center text-xs font-semibold",
                      tc.softBg,
                      tc.ink,
                    )}
                  >
                    softBg + ink
                  </div>
                  <code className="block text-[11px] text-muted-foreground">
                    {key}
                  </code>
                </div>
              );
            })}
          </div>
          <p className="max-w-prose text-xs text-muted-foreground">
            {t("styleGuide.thematicInterpolationWarning")}
          </p>
        </Section>
      </div>
    </div>
  );
}

export function StyleGuidePage() {
  return (
    <RequireCapability required={["backOffice"]}>
      <StyleGuide />
    </RequireCapability>
  );
}
